import { intlLocale } from "@/features/material-compliance/utils/date"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useOrganization } from "@/services/api/organizationApi"
import { useOrgSignatures } from "@/services/api/organizationSignaturesApi"

/** The city on the last line of the address, without its postcode: what a
 *  declaration prints when no place is set (the backend's
 *  `_issuer_location`). */
function cityFromAddress(address: string | null | undefined): string | null {
  const lines = (address ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
  const last = lines.at(-1)
  if (!last) return null
  const parts = last.split(/\s+/)
  return /^\d+$/.test(parts[0]) ? parts.slice(1).join(" ") || last : last
}

/**
 * The signature box at the foot of a declaration of conformity, as it
 * prints: place and issue date on one side, the signatory on the other (the
 * first of the user's own signatures, as an example).
 */
export function SignatureBoxPreview({ place }: { place: string }) {
  const { translate, currentLanguage } = useTranslation()
  const { data: organization } = useOrganization()
  const { data: signatures } = useOrgSignatures()
  const signatory = signatures?.[0]
  const printedPlace =
    place.trim() || cityFromAddress(organization?.addressMultiline) || "—"

  return (
    <div className="rounded-lg bg-white p-5 text-[11px] text-[#222] shadow-lg">
      <div className="flex gap-6">
        <div className="flex-1 border-t border-[#bdbdbd] pt-1.5 text-[#6f6f6f]">
          {printedPlace},{" "}
          {new Date().toLocaleDateString(intlLocale(currentLanguage))}
        </div>
        <div className="flex-1 border-t border-[#bdbdbd] pt-1.5 text-[#6f6f6f]">
          {signatory?.signatoryName ??
            translate("MC_ORG_SIG_PLACE_PREVIEW_SAMPLE_NAME")}
          {signatory?.signatoryFunction && (
            <div className="text-[10px]">{signatory.signatoryFunction}</div>
          )}
        </div>
      </div>
      <p className="mt-4 text-[10px] text-[#8a8a8a]">
        {translate("MC_ORG_SIG_PLACE_PREVIEW_ELECTRONIC_NOTE")}
      </p>
    </div>
  )
}
